import api from './api';
import {
  Form,
  FormSubmission,
  FormStructure,
  FormsResponse,
  FormResponse,
  SubmissionsResponse,
  SubmissionResponse,
  StatsResponse,
  PublicFormResponse,
  SubmitFormResponse,
  CreateFormRequest,
  UpdateFormRequest,
  SubmitFormRequest,
  UpdateSubmissionStatusRequest
} from '../types/Form';

// FORM MANAGEMENT API

export const getAllForms = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  formType?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<FormsResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
  if (params?.formType) queryParams.append('formType', params.formType);
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const response = await api.get(`/forms?${queryParams}`);
  return response.data;
};

export const getFormById = async (formId: number): Promise<FormResponse> => {
  const response = await api.get(`/forms/${formId}`);
  return response.data;
};

export const createForm = async (formData: CreateFormRequest): Promise<FormResponse> => {
  const response = await api.post('/forms', formData);
  return response.data;
};

export const createDefaultStudentForm = async (): Promise<FormResponse> => {
  const response = await api.post('/forms/create-default-student-form');
  return response.data;
};

export const getDefaultStudentTemplate = async (): Promise<{ structure: FormStructure }> => {
  const response = await api.get('/forms/default-student-template');
  return response.data;
};

export const updateForm = async (formId: number, formData: UpdateFormRequest): Promise<FormResponse> => {
  const response = await api.put(`/forms/${formId}`, formData);
  return response.data;
};

export const regenerateFormToken = async (formId: number): Promise<{ success: boolean; data: { publicToken: string } }> => {
  const response = await api.post(`/forms/${formId}/regenerate-token`);
  return response.data;
};

export const deleteForm = async (formId: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/forms/${formId}`);
  return response.data;
};

export const uploadFormBanner = async (formId: number, file: File): Promise<{ success: boolean; data: { bannerUrl: string } }> => {
  const formData = new FormData();
  formData.append('banner', file);
  const response = await api.post(`/forms/${formId}/banner`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const removeFormBanner = async (formId: number): Promise<{ success: boolean; data: { bannerUrl: null } }> => {
  const response = await api.delete(`/forms/${formId}/banner`);
  return response.data;
};

// FORM SUBMISSION MANAGEMENT API

export const getFormSubmissions = async (formId: number, params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<SubmissionsResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const response = await api.get(`/forms/${formId}/submissions?${queryParams}`);
  return response.data;
};

export const getAllSubmissions = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  formId?: number;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<SubmissionsResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.formId) queryParams.append('formId', params.formId.toString());
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const response = await api.get(`/forms/submissions/all?${queryParams}`);
  return response.data;
};

export const getSubmissionById = async (submissionId: number): Promise<SubmissionResponse> => {
  const response = await api.get(`/forms/submissions/${submissionId}`);
  return response.data;
};

export const updateSubmissionStatus = async (
  submissionId: number, 
  statusData: UpdateSubmissionStatusRequest
): Promise<SubmissionResponse> => {
  const response = await api.put(`/forms/submissions/${submissionId}/status`, statusData);
  return response.data;
};

export const deleteSubmission = async (submissionId: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/forms/submissions/${submissionId}`);
  return response.data;
};

export const getSubmissionStats = async (): Promise<StatsResponse> => {
  const response = await api.get('/forms/stats/overview');
  return response.data;
};

// PUBLIC FORM API (No authentication required)
// Use REACT_APP_API_URL or same-origin when in browser; no hardcoded IP.
function getPublicApiBase(): string {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location?.origin) return `${window.location.origin}/api`;
  return '/api';
}

export const getPublicForm = async (token: string): Promise<PublicFormResponse> => {
  const baseUrl = getPublicApiBase();
  const response = await fetch(`${baseUrl}/forms/public/${token}`);
  if (!response.ok) {
    throw new Error('Failed to fetch form');
  }
  return response.json();
};

export const submitPublicForm = async (token: string, submissionData: SubmitFormRequest): Promise<SubmitFormResponse> => {
  const baseUrl = getPublicApiBase();
  const response = await fetch(`${baseUrl}/forms/public/${token}/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(submissionData),
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to submit form');
  }
  
  return response.json();
};

// UTILITY FUNCTIONS

export const generatePublicFormUrl = (token: string): string => {
  const baseUrl =
    process.env.REACT_APP_PUBLIC_URL ||
    (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : null) ||
    (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '');
  return `${baseUrl || ''}/public-form/${token}`;
};

export const copyFormUrlToClipboard = async (token: string): Promise<boolean> => {
  try {
    const url = generatePublicFormUrl(token);
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'submitted':
      return 'text-blue-600 bg-blue-100';
    case 'under_review':
      return 'text-yellow-600 bg-yellow-100';
    case 'approved':
      return 'text-green-600 bg-green-100';
    case 'rejected':
      return 'text-red-600 bg-red-100';
    case 'attached':
      return 'text-purple-600 bg-purple-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'submitted':
      return 'Submitted';
    case 'under_review':
      return 'Under Review';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'attached':
      return 'Attached';
    default:
      return 'Unknown';
  }
};

export const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'submitted':
      return 'ClockIcon';
    case 'under_review':
      return 'ClockIcon';
    case 'approved':
      return 'CheckCircleIcon';
    case 'rejected':
      return 'XCircleIcon';
    case 'attached':
      return 'DocumentTextIcon';
    default:
      return 'ClockIcon';
  }
};
